import { DatePicker, Form, Select } from 'antd';
import React from 'react';
import DrawerButtons from '../../../components/drawer-buttons';
import {
    ApiException,
    CompaniesClient,
    CompanyVm,
    CopySalariesCommand,
    SalariesClient,
    SalaryVm,
} from '../../../utils/api';
import { Props } from '.';
import { translations } from '../../../config/translations';
import SelectMonth from '../../../components/select-month';
import moment from 'moment';
import { SelectOption } from '../../../core/models/SelectOption';
import { formItemLayout420 } from '../../../config/formLayouts';
import { showError, showSuccess } from '../../../helpers/NotificationHelper';

interface State {
    isSaving: boolean;
    companies: SelectOption[];
}

class SalariesCopyForm extends React.Component<Props, State> {
    public constructor(props: Props) {
        super(props);

        this.state = {
            isSaving: false,
            companies: [],
        };
    }

    public componentDidMount = () => {
        this.getCompanies();
    };

    private getCompanies = async () => {
        const companies = (await new CompaniesClient().getAll()).map(
            (unit: CompanyVm): SelectOption => ({
                label: unit.name,
                value: unit.id,
            })
        );

        if (companies.length > 0) {
            this.setState({
                companies: companies,
            });
        }
    };

    private handleSubmit = async (values: any) => {
        this.setState({
            isSaving: true,
        });

        const request: CopySalariesCommand = new CopySalariesCommand({
            companyId: values.companyId,
            fromMonth: values.fromMonth,
            fromYear: values.fromYear.year(),
            toMonth: values.toMonth,
            toYear: values.toYear.year(),
        });

        try {
            const result = await new SalariesClient().copyBatch(request);

            this.handleSuccess(result);
        } catch (error) {
            if (error instanceof ApiException) {
                showError(error.response);
            } else {
                showError(translations.general.errorSavingData);
            }
        }

        this.setState({
            isSaving: false,
        });
    };

    private handleSuccess = (result: SalaryVm[]) => {
        const { onSuccess } = this.props;

        showSuccess(translations.salaries.successfullyCopied);

        onSuccess(result);
    };

    public render(): React.ReactElement {
        const { onClose, companyId } = this.props;
        const { isSaving, companies } = this.state;

        const currentDate = moment();
        const nextMonthDate = moment().add(1, 'month');

        return (
            <Form
                onFinish={this.handleSubmit}
                style={{ textAlign: 'end' }}
                title={translations.salaries.copySalaries}
                {...formItemLayout420}
                initialValues={{
                    companyId: companyId,
                    fromMonth: currentDate.month() + 1, // current month, moment has 0-based index
                    fromYear: currentDate,
                    toMonth: nextMonthDate.month() + 1, // next month, moment has 0-based index
                    toYear: nextMonthDate,
                }}
            >
                <DrawerButtons
                    isSaving={isSaving}
                    onCancelAction={onClose}
                    submitText={translations.salaries.copy}
                />

                <Form.Item
                    name="companyId"
                    label={translations.salaries.company}
                    rules={[
                        {
                            required: true,
                            message: translations.general.requiredField,
                        },
                    ]}
                >
                    <Select<number>
                        placeholder={translations.employees.companyPlaceholder}
                        options={companies}
                        style={{ width: '100%', textAlign: 'left' }}
                    />
                </Form.Item>

                <Form.Item
                    label={translations.general.copyFrom}
                    style={{ marginBottom: 0 }}
                    required
                >
                    <Form.Item
                        name="fromMonth"
                        style={{ display: 'inline-block', width: 'calc(50% - 4px)' }}
                        rules={[
                            {
                                required: true,
                                message: translations.general.requiredField,
                            },
                        ]}
                    >
                        <SelectMonth width={'100%'} />
                    </Form.Item>
                    <Form.Item
                        name="fromYear"
                        style={{
                            display: 'inline-block',
                            width: 'calc(50% - 4px)',
                            marginLeft: '8px',
                        }}
                        rules={[
                            {
                                required: true,
                                message: translations.general.requiredField,
                            },
                        ]}
                    >
                        <DatePicker picker="year" style={{ width: '100%' }} />
                    </Form.Item>
                </Form.Item>

                <Form.Item label={translations.general.copyTo} style={{ marginBottom: 0 }} required>
                    <Form.Item
                        name="toMonth"
                        style={{ display: 'inline-block', width: 'calc(50% - 4px)' }}
                        rules={[
                            {
                                required: true,
                                message: translations.general.requiredField,
                            },
                        ]}
                    >
                        <SelectMonth width={'100%'} />
                    </Form.Item>
                    <Form.Item
                        name="toYear"
                        style={{
                            display: 'inline-block',
                            width: 'calc(50% - 4px)',
                            marginLeft: '8px',
                        }}
                        rules={[
                            {
                                required: true,
                                message: translations.general.requiredField,
                            },
                        ]}
                    >
                        <DatePicker picker="year" style={{ width: '100%' }} />
                    </Form.Item>
                </Form.Item>
            </Form>
        );
    }
}

export default SalariesCopyForm;
